import { all, call, fork, put, takeEvery } from 'redux-saga/effects';
import {
	getCurrentUser,
	setCurrentUser,
	setLocalPermissions,
	setRolesAndPermissions,
	setSubscriptionsList,
} from '@/helpers/AuthHelper';
import { defaultReportFilter, defaultUser } from '@/configs/main';
import { auth, localAuth, localData } from '@/services';
import { isProxied } from '@/helpers/Utils';

import { changeVersionAuth } from '@/redux/versionAuth/actions';
import versionAuth from '@/configs/authVersion';
import { setReportFilter } from '@/helpers/WorkspaceHelper';
import moment from 'moment-timezone';
import {
	loginUserSuccess,
	loginUserError,
	resetPasswordSuccess,
	resetPasswordError,
	changePasswordSuccess,
	changePasswordError,
	bindMobileSuccess,
	bindMobileError,
	changeMerchant,
	changeBranch,
	changeBranchIds,
	changeSubscriptions,
	changeAssignedLicenses,
	clearWorkspace,
} from '../actions';
import {
	LOGIN_USER_EMBEDDED,
	LOGIN_LOCAL_USER,
	LOGIN_USER,
	LOGOUT_USER,
	RESET_PASSWORD,
	CHANGE_PASSWORD,
	BIND_MOBILE,
	LOGIN_USER_SSO,
	LOGIN_USER_SSO2,
} from '../actions-registry';

// General Login
const loginWithEmailPasswordAsync = (email, password) =>
	auth
		.login(email, password)
		.then((data) => data)
		.catch((error) => {
			throw error;
		});

const loginSSOAsync = (payload) =>
	auth
		.sso(payload)
		.then((data) => data)
		.catch((error) => {
			throw error;
		});

const getProfileAsync = (token) =>
	auth
		.getProfile(token)
		.then((data) => data)
		.catch((error) => {
			throw error;
		});

function* loginWithEmailPassword({ payload }) {
	const {
		user: { email, password },
		history,
		redirectUrl,
	} = payload;
	try {
		const user = yield call(
			loginWithEmailPasswordAsync,
			email.toLowerCase(),
			password
		);
		const profile = yield call(getProfileAsync, user.token);
		const item = {
			...defaultUser,
			...profile,
			token: user.token,
			tempPass: profile.isTempPass ? password : null,
		};
		setCurrentUser(item);
		let next = '';
		if (item.isTempPass) {
			item.tempPass = password;
			next = {
				pathname: '/init-password',
				state: { from: redirectUrl },
			};
		} else {
			next = redirectUrl;
		}
		yield put(loginUserSuccess(item));
		history.push(next);
	} catch (error) {
		yield put(loginUserError(error.message));
	}
}

export function* watchLoginUser() {
	yield takeEvery(LOGIN_USER, loginWithEmailPassword);
}

function* loginSSO({ payload }) {
	const { id, mid, type, token, history, redirectUrl } = payload;
	try {
		const user = yield call(loginSSOAsync, {
			userId: id,
			merchantId: mid,
			userType: type,
			verifyToken: token,
		});
		const profile = yield call(getProfileAsync, user.token);
		const item = {
			...defaultUser,
			...profile,
			token: user.token,
		};
		if (type === '1') {
			item.id = -1 * id;
		}
		setCurrentUser(item);
		yield put(loginUserSuccess(item));
		sessionStorage.setItem('sso_mid', mid);
		history.push(redirectUrl);
	} catch (error) {
		yield put(loginUserError(error.message));
	}
}

export function* watchLoginSSO() {
	yield takeEvery(LOGIN_USER_SSO, loginSSO);
}

function* loginSSO2({ payload }) {
	try {
		setCurrentUser(payload);
		yield put(loginUserSuccess(payload));
	} catch (error) {
		yield put(loginUserError(error.message));
	}
}

export function* watchLoginSSO2() {
	yield takeEvery(LOGIN_USER_SSO2, loginSSO2);
}

// Access logged in user when be embedded to an authorized app.
function* loginEmbedded({ payload }) {
	setCurrentUser(payload);
	yield put(loginUserSuccess(payload));
}

export function* watchLoginUserEmbedded() {
	yield takeEvery(LOGIN_USER_EMBEDDED, loginEmbedded);
}

const loginLocalUserAsync = (username, password) =>
	localAuth
		.login(username, password)
		.then((data) => data)
		.catch((error) => {
			throw error;
		});

const getGatewayTokenForLocalUserAsync = (token) =>
	localAuth
		.gatewayToken(token)
		.then((data) => data)
		.catch((error) => {
			throw error;
		});

const getStoreProfileAsync = (token) =>
	localData
		.profile(token)
		.then((data) => data)
		.catch((error) => {
			throw error;
		});

function* loginLocalUser({ payload }) {
	const {
		user: { username, password },
		history,
		redirectUrl,
	} = payload;
	try {
		const user = yield call(loginLocalUserAsync, username, password);
		const gatewayToken = yield call(
			getGatewayTokenForLocalUserAsync,
			user.Token
		);
		const profile = yield call(getStoreProfileAsync, user.token);
		const item = {
			...defaultUser,
			id: user.id,
			username,
			token: user.Token,
			gatewayToken: gatewayToken.Token,
			type: 'local',
		};
		setCurrentUser(item);
		setLocalPermissions(user.Permissions.map((el) => el.Permission));
		yield put(loginUserSuccess(item));

		const timezone = moment.tz.guess();
		yield put(
			changeMerchant({
				merchantId: profile.MerchantId,
				merchantName: '',
				currency: profile.Currency ?? 'AUD',
				type: 0,
				branches: [
					{
						branchId: profile.BranchId,
						branchName: '',
						timezone,
						groupName: '',
						enableCloudMenu: profile.EnableCloudMenu,
						canModifyCloudMenu: profile.CanModifyCloudMenu,
					},
				],
			})
		);
		yield put(
			changeBranch({
				branchId: profile.BranchId,
				branchName: '',
				timezone,
				groupName: '',
				enableCloudMenu: profile.EnableCloudMenu,
				canModifyCloudMenu: profile.CanModifyCloudMenu,
				configType: user.ConfigType,
			})
		);
		yield put(changeBranchIds(profile.BranchId));
		const subs = user.Subscriptions.map((el) => el.ProductCode);
		yield put(changeSubscriptions(subs));
		yield put(
			changeAssignedLicenses([{ branchId: profile.BranchId, licenses: subs }])
		);

		const authVersion = user.Subscriptions.find(
			(el) => el.ProductCode === 'zii-pos'
		)?.PriceProductType;
		let obj = {
			auth_state: authVersion === 'Standard',
		};
		if (!authVersion) {
			obj = {
				...obj,
				...versionAuth,
			};
		}
		setSubscriptionsList(
			user.Subscriptions.find((el) => el.ProductCode === 'zii-pos')
		);
		yield put(changeVersionAuth({ ...obj }));
		history.push(redirectUrl);
	} catch (error) {
		yield put(loginUserError(error.message));
	}
}
export function* watchLoginLocalUser() {
	yield takeEvery(LOGIN_LOCAL_USER, loginLocalUser);
}

// Logout
const logoutAsync = async (history) => {
	if (isProxied()) {
		history.push('/instore-login');
	} else {
		history.push('/login');
	}
};

function* logout({ payload }) {
	const { history } = payload;
	setCurrentUser(null);
	setRolesAndPermissions(null);
	setLocalPermissions(null);
	setReportFilter(defaultReportFilter);
	yield put(clearWorkspace());
	yield call(logoutAsync, history);
}

export function* watchLogoutUser() {
	yield takeEvery(LOGOUT_USER, logout);
}

// Request reset password if forgot it.
const resetPasswordAsync = async (email, password, code) =>
	auth
		.resetPassword(email, password, code)
		.then((data) => data)
		.catch((error) => {
			throw error;
		});

function* resetPassword({ payload }) {
	const { email, password, code, history } = payload;
	try {
		yield call(resetPasswordAsync, email, password, code);
		yield put(resetPasswordSuccess());
		history.push('/login');
	} catch (error) {
		yield put(resetPasswordError(error.message));
	}
}

export function* watchResetPassword() {
	yield takeEvery(RESET_PASSWORD, resetPassword);
}

// Change password
const changePasswordAsync = async (oldPassword, newPassword) =>
	auth
		.changePassword(oldPassword, newPassword)
		.then((data) => data)
		.catch((error) => {
			throw error;
		});

function* changePassword({ payload }) {
	const { oldPassword, newPassword, history, redirectUrl, init } = payload;
	try {
		yield call(changePasswordAsync, oldPassword, newPassword);
		const user = getCurrentUser();
		setCurrentUser({ ...user, isTempPass: false, tempPass: null });
		yield put(changePasswordSuccess(init));
		history.push(redirectUrl || '/');
	} catch (error) {
		yield put(changePasswordError(error.message));
	}
}

export function* watchChangePassword() {
	yield takeEvery(CHANGE_PASSWORD, changePassword);
}

const bindMobileAsync = async (mobile, code, region) =>
	auth
		.bindMobile(mobile, code, region)
		.then((data) => data)
		.catch((error) => {
			throw error;
		});

function* bindMobile({ payload }) {
	const { mobile, code, region, history } = payload;
	try {
		yield call(bindMobileAsync, mobile, code, region);
		const user = getCurrentUser();
		setCurrentUser({ ...user, phone: mobile, phoneRegion: region });
		yield put(bindMobileSuccess({ mobile, region }));
		history.push('/');
	} catch (error) {
		yield put(bindMobileError(error.message));
	}
}

export function* watchBindMobile() {
	yield takeEvery(BIND_MOBILE, bindMobile);
}

export default function* rootSaga() {
	yield all([
		fork(watchLoginUser),
		fork(watchLoginLocalUser),
		fork(watchLoginUserEmbedded),
		fork(watchLogoutUser),
		fork(watchResetPassword),
		fork(watchChangePassword),
		fork(watchBindMobile),
		fork(watchLoginSSO),
		fork(watchLoginSSO2),
	]);
}
